import { Button, Icon, IconButton, Text, Title } from "@assetwatch/ui";
import { useTranslation } from "react-i18next";
import type { AskReliFloatingFallbackProps } from "./AskReli.types";
import { EnterpriseCustomerSelectionCard } from "./EnterpriseCustomerState";

type AskReliFloatingFallbackShellProps = {
  description: string;
  isExpanded: boolean;
  children: React.ReactNode;
  onClose: () => void;
  onToggleExpand: () => void;
};

function AskReliFloatingFallbackShell({
  description,
  isExpanded,
  children,
  onClose,
  onToggleExpand,
}: AskReliFloatingFallbackShellProps) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-(--radius-xl) border border-outline-subtle bg-surface shadow-(--effect-elevation-6)">
      <div className="flex items-center gap-(--spacing-10) border-b border-outline-subtle bg-surface px-(--spacing-16) py-(--spacing-12)">
        <div className="flex size-(--spacing-32) items-center justify-center rounded-full bg-primary/[0.12] text-primary">
          <Icon name="sparkles" size="sm" tone="inherit" aria-hidden />
        </div>
        <div className="min-w-0">
          <Title size="sm" as="h2">
            {t("enterprise.reliability.assistant.title")}
          </Title>
          <Text size="sm" tone="subtle">
            {description}
          </Text>
        </div>
        <div className="ml-auto flex items-center gap-(--spacing-4)">
          <IconButton
            icon="expand"
            variant="ghost"
            size="sm"
            aria-label={
              isExpanded
                ? t("enterprise.reliability.assistant.shrinkChat")
                : t("enterprise.reliability.assistant.expandChat")
            }
            onClick={onToggleExpand}
          />
          <IconButton
            icon="x-close"
            variant="ghost"
            size="sm"
            aria-label={t("enterprise.reliability.assistant.closeChat")}
            onClick={onClose}
          />
        </div>
      </div>
      {children}
    </div>
  );
}

export function AskReliFloatingCustomerSelectionFallback({
  isExpanded,
  onClose,
  onToggleExpand,
}: AskReliFloatingFallbackProps) {
  const { t } = useTranslation();

  return (
    <AskReliFloatingFallbackShell
      description={t("enterprise.reliability.assistant.fallbackSelectCustomer")}
      isExpanded={isExpanded}
      onClose={onClose}
      onToggleExpand={onToggleExpand}
    >
      <div className="min-h-0 flex-1 overflow-y-auto p-(--spacing-16)">
        <EnterpriseCustomerSelectionCard
          description={t(
            "enterprise.reliability.assistant.fallbackSelectCustomerCard",
          )}
        />
      </div>
    </AskReliFloatingFallbackShell>
  );
}

export function AskReliFloatingErrorFallback({
  isExpanded,
  onClose,
  onRetry,
  onToggleExpand,
}: AskReliFloatingFallbackProps & {
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  return (
    <AskReliFloatingFallbackShell
      description={t("enterprise.reliability.assistant.fallbackError")}
      isExpanded={isExpanded}
      onClose={onClose}
      onToggleExpand={onToggleExpand}
    >
      <div className="flex min-h-0 flex-1 flex-col items-start justify-center gap-(--spacing-12) p-(--spacing-16)">
        <Text>{t("enterprise.reliability.assistant.fallbackErrorBody")}</Text>
        <Button type="button" variant="outline" onClick={onRetry}>
          {t("enterprise.reliability.assistant.tryAgain")}
        </Button>
      </div>
    </AskReliFloatingFallbackShell>
  );
}
